import { t } from "i18next";
import type { JSX, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import MicrophoneIcon from "@/assets/icons/mic.svg?react";
import { AppConfig } from "@/common/appConfig";
import { useLiveShortcutCues } from "@/hooks/visits/useLiveShortcutCues";
import { Button } from "../button";
import ConfirmDialog from "../ConfirmDialog";
import { LoadingSpinner } from "../loading-spinner";
import { V3ConfirmSheet } from "../v3/confirm-sheet";
import { ClassicRecordingInstructions } from "../visits/ClassicRecordingInstructions";
import { RecordingConsentCheck } from "../visits/RecordingConsentCheck";
import RecorderAudioPlayer from "./recorder-audio-player";
import RecorderControls from "./recorder-controls";
import { RecorderStopSheet } from "./recorder-stop-sheet";
import { RecorderV32 } from "./recorder-v32";
import RecorderVisualizer from "./recorder-visualizer";
import { TranscriptionStreamingList } from "./TranscriptionStreamingList";
import { formatTime } from "./utils/format-timer";

const RecorderUIInner = ({
  status,
  startRecording,
  stopRecording,
  pauseRecording,
  resumeRecording,
  mediaBlobUrl,
  mediaStream,
  autoStarted,
  setAutoStarted,
  recordingTime,
  setRecordingTime,
  showConfirmation,
  setShowConfirmation,
  finished,
  setFinished,
  isRealtimeEnabled,
  newVisit = false,
  onDiscard,
  previewLevels,
  noteSettings,
  added,
  notice,
}: {
  status: string;
  startRecording: () => void;
  stopRecording: () => void;
  pauseRecording: () => void;
  resumeRecording: () => void;
  mediaBlobUrl: string | null;
  mediaStream: MediaStream | null;
  autoStarted: boolean;
  setAutoStarted: (value: boolean) => void;
  recordingTime: number;
  setRecordingTime: (value: number | ((previous: number) => number)) => void;
  showConfirmation: boolean;
  setShowConfirmation: (value: boolean) => void;
  finished: boolean;
  setFinished: (value: boolean) => void;
  isRealtimeEnabled: boolean;
  /** "Add instruction" chips (new visits only, behind the flag). */
  /** New visit: VITE_RECORDER_V32_ENABLED layout, recording instructions. */
  newVisit?: boolean;
  /** New visit: discard the recording (after the recorder has asked). */
  onDiscard?: () => void;
  /** Stories only: fixed waveform levels. */
  previewLevels?: number[];
  /** v3.2 recorder: the note template chip. */
  noteSettings?: ReactNode;
  /**
   * A recording added to an existing visit (Add recording, Start visit) in
   * the v3.2 layout: its title, and the consent chip for a pre-chart's first
   * recording. No recording instructions (they belong to a new visit).
   */
  added?: { title: string; subtitle?: string; consent?: boolean };
  /** v3.2 layout: a notice under the recorder's top bar. */
  notice?: ReactNode;
}): JSX.Element => {
  const v32Layout = AppConfig.enableRecorderV32 && (newVisit || !!added);
  // Stop pauses, then asks. Cancelling the question carries on recording if
  // it was running, so it never stays paused without the doctor choosing to.
  const resumeAfterCancel = useRef(false);
  const stopConfirmed = useRef(false);
  const askToStop = (): void => {
    resumeAfterCancel.current = status === "recording";
    stopConfirmed.current = false;
    pauseRecording();
    setShowConfirmation(true);
  };
  const onConfirmationChange = (open: boolean): void => {
    setShowConfirmation(open);
    if (open) return;
    // The confirm button closes the dialog too (before or after onConfirm,
    // depending on the dialog): decide once both have run.
    window.setTimeout(() => {
      if (!stopConfirmed.current && resumeAfterCancel.current) {
        resumeAfterCancel.current = false;
        resumeRecording();
      }
    }, 0);
  };
  useEffect(() => {
    if (!autoStarted) {
      startRecording();
      setAutoStarted(true);
    }
  }, [autoStarted, startRecording, setAutoStarted]);

  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | undefined;
    if (status === "recording") {
      timer = setInterval(() => {
        setRecordingTime((previous) => previous + 1);
      }, 1000);
    }
    return (): void => {
      if (timer) clearInterval(timer);
    };
  }, [status, setRecordingTime]);

  const handleConfirmYes = (): void => {
    stopConfirmed.current = true;
    resumeAfterCancel.current = false;
    stopRecording();
    setFinished(true);
    setShowConfirmation(false);
  };

  // Discard (new visits): Back, Stop's "Discard recording" or the classic
  // Discard button ask "Discard this recording?" first. Cancelling
  // it after Stop carries on recording, as cancelling Stop does.
  const [askDiscard, setAskDiscard] = useState(false);
  const resumeAfterDiscardCancel = useRef(false);
  const discarded = useRef(false);
  const requestDiscard = (): void => {
    resumeAfterDiscardCancel.current = false;
    discarded.current = false;
    setAskDiscard(true);
  };
  const discardFromStop = (): void => {
    // Leaving Stop's sheet for the discard question is not a cancel.
    stopConfirmed.current = true;
    resumeAfterDiscardCancel.current = resumeAfterCancel.current;
    resumeAfterCancel.current = false;
    discarded.current = false;
    setShowConfirmation(false);
    setAskDiscard(true);
  };
  const onDiscardChange = (open: boolean): void => {
    setAskDiscard(open);
    if (open) return;
    window.setTimeout(() => {
      if (!discarded.current && resumeAfterDiscardCancel.current) {
        resumeAfterDiscardCancel.current = false;
        resumeRecording();
      }
    }, 0);
  };
  const confirmDiscard = (): void => {
    discarded.current = true;
    resumeAfterDiscardCancel.current = false;
    setAskDiscard(false);
    onDiscard?.();
  };
  const discardQuestion = !onDiscard ? null : !v32Layout ? (
    <ConfirmDialog
      cancelText={t("recorderV32.discardConfirm.cancel")}
      confirmText={t("recorderV32.discardConfirm.confirm")}
      description={t("recorderV32.discardConfirm.description")}
      open={askDiscard}
      title={t("recorderV32.discardConfirm.title")}
      onConfirm={confirmDiscard}
      onOpenChange={onDiscardChange}
    />
  ) : (
    <V3ConfirmSheet
      destructive
      cancelLabel={t("recorderV32.discardConfirm.cancel")}
      confirmLabel={t("recorderV32.discardConfirm.confirm")}
      description={t("recorderV32.discardConfirm.description")}
      open={askDiscard}
      testId="recorder-discard-confirm"
      title={t("recorderV32.discardConfirm.title")}
      onConfirm={confirmDiscard}
      onOpenChange={onDiscardChange}
    />
  );

  // The new-visit recorder in the v3.2 layout (VITE_RECORDER_V32_ENABLED),
  // with the same controls and confirmation.
  if (!finished && v32Layout) {
    return (
      <>
        <RecorderV32
          consent={
            AppConfig.enableRecordingConsent && (newVisit || !!added?.consent)
          }
          instructions={newVisit && AppConfig.enableRecordingInstructions}
          subtitle={added?.subtitle}
          title={added?.title}
          isRealtimeEnabled={isRealtimeEnabled}
          mediaStream={mediaStream}
          noteSettings={noteSettings}
          notice={notice}
          previewLevels={previewLevels}
          recordingTime={recordingTime}
          status={status}
          onPause={pauseRecording}
          onRequestDiscard={onDiscard ? requestDiscard : undefined}
          onResume={resumeRecording}
          onStop={askToStop}
        />
        <RecorderStopSheet
          open={showConfirmation}
          onDiscard={onDiscard ? discardFromStop : undefined}
          onOpenChange={onConfirmationChange}
          onStop={handleConfirmYes}
        />
        {discardQuestion}
      </>
    );
  }

  if (!finished) {
    return (
      <div className="w-full gap-4 justify-center items-center flex flex-col relative">
        <MicrophoneIcon
          aria-label={t("components.microphoneButton.label")}
          height={100}
          strokeWidth="0.5"
          width={100}
        />
        <RecorderVisualizer audio={mediaStream} paused={status === "paused"} />
        <p className="font-medium">{formatTime(recordingTime)}</p>
        <ConfirmDialog
          description={t("components.recorderInner.finishAudioDescription")}
          open={showConfirmation}
          title={t("components.recorderInner.finishAudioTitle")}
          onConfirm={handleConfirmYes}
          onOpenChange={onConfirmationChange}
        />
        {!showConfirmation && (
          <div className="w-full space-y-4">
            <RecorderControls
              status={status}
              onPause={pauseRecording}
              onResume={resumeRecording}
              onStopAndSave={askToStop}
            />
            {newVisit && AppConfig.enableRecordingConsent && (
              <RecordingConsentCheck className="pt-2" />
            )}
            {newVisit && AppConfig.enableRecordingInstructions && (
              <ClassicInstructions
                isRealtimeEnabled={isRealtimeEnabled}
                recordingTime={recordingTime}
              />
            )}
            {onDiscard && (
              <div className="flex justify-center">
                <Button
                  className="text-destructive hover:text-destructive"
                  variant="ghost"
                  onClick={requestDiscard}
                >
                  {t("components.recorderControls.discardButton")}
                </Button>
              </div>
            )}
            {isRealtimeEnabled && <TranscriptionStreamingList />}
          </div>
        )}
        {discardQuestion}
      </div>
    );
  }

  if (!mediaBlobUrl) {
    return <LoadingSpinner size={8} />;
  }

  return <RecorderAudioPlayer mediaBlobUrl={mediaBlobUrl} />;
};

export default RecorderUIInner;

// Recording instructions in the classic recorder, with "Will insert" when a
// live transcript is there. Its own component so the hooks only run with the
// feature on.
const ClassicInstructions = ({
  isRealtimeEnabled,
  recordingTime,
}: {
  isRealtimeEnabled: boolean;
  recordingTime: number;
}): JSX.Element => {
  const cues = useLiveShortcutCues(isRealtimeEnabled, recordingTime);
  return <ClassicRecordingInstructions className="pt-2" cues={cues} />;
};
